import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

import { getActiveBinding } from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { BlazeSchema } from '@blaze-shared/schema-domain';
import { findFirstNodeInWorkflowOfType, isGetNode } from '@blaze-shared/workflow-domain';

import { RuntimeFacade } from '../../../+state';
import { getUiRuntimeElement } from '../../../utils/runtime';
import { ChatElement } from '../chat-element.models';

@Injectable({
  providedIn: 'root',
})
export class ChatSchemaService {
  private readonly runtimeFacade = inject(RuntimeFacade);
  private readonly schemaService = inject(SchemaService);

  allInputPropsReady(element: ChatElement): boolean {
    if (
      !element.props?.content?.input?.messages ||
      !element.props?.content?.input?.conversationsInfo ||
      !element.props.content.input?.conversations
    ) {
      return false;
    }

    const { messages, conversations, conversationsInfo } = element.props.content.input;

    const messagesInputReady = [
      messages.messageBody,
      messages.id,
      messages.conversation,
      messages.sender,
      messages.createdTime,
      messages.updatedTime,
      messages.attachments,
    ].every(Boolean);
    const conversationsInputReady = [
      conversations.id,
      conversations.name,
      conversations.members,
      conversations.createdBy,
    ].every(Boolean);
    const conversationsInfoInputReady = [
      conversationsInfo.conversation,
      conversationsInfo.lastMessageId,
      conversationsInfo.membersEmail,
      conversationsInfo.membersImage,
      conversationsInfo.membersName,
    ].every(Boolean);

    return messagesInputReady && conversationsInputReady && conversationsInfoInputReady;
  }

  getDatasetSchema$(
    element: ChatElement,
    dataSourceKey: 'messages' | 'conversations' | 'conversationsInfo' | 'members',
  ): Observable<BlazeSchema | undefined> {
    const dataSourceId = this.getBlazeTableId(element, dataSourceKey);
    if (!dataSourceId) {
      return of(undefined);
    }

    return this.schemaService.get(dataSourceId);
  }

  getDatasetSchema(
    element: ChatElement,
    dataSourceKey: 'messages' | 'conversations' | 'conversationsInfo' | 'members',
  ): BlazeSchema | undefined {
    const dataSourceId = this.getBlazeTableId(element, dataSourceKey);

    if (!dataSourceId) {
      return;
    }

    return this.runtimeFacade.getSchemaById(dataSourceId);
  }

  getBlazeTableId(
    element: ChatElement,
    dataSourceKey: 'messages' | 'conversations' | 'conversationsInfo' | 'members',
  ): string | undefined {
    const dataSourceId = getActiveBinding(element.props.data, dataSourceKey)?.dataSource?.id;

    if (!dataSourceId) {
      return;
    }

    const workflow = this.runtimeFacade
      .getWorkflowsByTarget(getUiRuntimeElement(element.id))
      .find((w) => w.id === dataSourceId);

    if (!workflow) {
      return dataSourceId;
    }

    const getNode = findFirstNodeInWorkflowOfType(workflow, isGetNode);
    return getNode?.datasetId;
  }
}
